import moment from 'moment';
import * as swal from '@/helpers/swal';
import * as dateFormat from '@/helpers/dateFormat';
import { CFMCoreEnumsEnumFreezeStatus, CFMDTOFreezeView, CFMDTOFreezeWindow } from '@/scripts/services/ClientApiAuto';

/**
 * Freeze (degisiklik dondurma) donemlerinin ekranlar arasinda ortak gosterimi ve planlama uyarisi.
 *
 * Freeze bir ENGEL degil, uyaridir: denk gelen donemler listelenir, kullanici onaylarsa kayit
 * yapilir. Is emri kaydi ve personel atamasi buradaki uyariyi KULLANMAZ: orada sunucu ayni uyariyi
 * forceSave akisinin onay mesajina kendisi ekliyor. Buradaki onay, forceSave akisi olmayan bakim
 * plani ekranlari ve bakim takvimi icindir.
 */

/** Sunucudaki EnumFreezeStatus ile ayni degerler. */
export const FreezeStatus = {
    Planned: 1,
    Active: 2,
    Completed: 3,
} as const;

export const FREEZE_STATUS_VIEW: Record<CFMCoreEnumsEnumFreezeStatus, { label: string, badge: string }> = {
    [FreezeStatus.Planned]: { label: 'freeze_status_planned', badge: 'm-badge--info' },
    [FreezeStatus.Active]: { label: 'freeze_status_active', badge: 'm-badge--success' },
    [FreezeStatus.Completed]: { label: 'freeze_status_completed', badge: 'm-badge--metal' },
};

export const FREEZE_DATE_FORMAT = 'DD.MM.YYYY HH:mm';

/** "01.06.2027 08:00 - 03.06.2027 08:00 (aciklama)"; duz metin. */
export const formatFreezePeriod = (start: Date, end: Date, description?: string | null) => {
    const period = `${moment(start).format(FREEZE_DATE_FORMAT)} - ${moment(end).format(FREEZE_DATE_FORMAT)}`;
    return description ? `${period} (${description})` : period;
};

/** Sunucudan gelen tarihler duvar saati; bkz. dateFormat.parseWallClock. */
const formatFreezeView = (freeze: CFMDTOFreezeView) => formatFreezePeriod(
    <Date>dateFormat.parseWallClock(freeze.StartDate),
    <Date>dateFormat.parseWallClock(freeze.EndDate),
    freeze.Description,
);

const HTML_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Aciklama kullanici girdisi ve onay penceresi HTML gosteriyor. */
export const escapeHtml = (text: string) => text.replace(/[&<>"']/g, ch => HTML_ESCAPES[ch]);

/**
 * Freeze listesi donduren bir istegin sonucu; cevap alinamazsa null, boylece cagiran "bilinmiyor"
 * ile "freeze yok"u ayirabilir. Uyari kaydin kendisinden onemli degil: hata yutulur.
 */
export async function fetchFreezes(
    request: Promise<{ data: { IsSuccess?: boolean, Data?: CFMDTOFreezeView[] | null } }>,
): Promise<CFMDTOFreezeView[] | null> {
    try {
        const response = await request;
        return response.data.IsSuccess ? response.data.Data || [] : null;
    } catch (e) {
        console.warn('Freeze donemleri alinamadi', e);
        return null;
    }
}

/** Denk gelen donem yoksa sormadan devam eder; varsa donemleri listeleyip kullaniciya sorar. */
export async function confirmFreezes(vm: any, freezes: CFMDTOFreezeView[] | null | undefined): Promise<boolean> {
    if (!freezes || freezes.length === 0) {
        return true;
    }
    const html = `${vm.translate('freeze_conflict_message')}<br/>`
        + freezes.map(freeze => escapeHtml(formatFreezeView(freeze))).join('<br/>')
        + `<br/><br/>${vm.translate('freeze_conflict_question')}`;
    const answer = await swal.checkHtml(vm, vm.translate('freeze_conflict_title'), html);
    return !!answer.value;
}

/** Denk gelen donemleri donduren istegi bekleyip onay ister; istek basarisizsa sormadan devam eder. */
export async function confirmFreezesFrom(vm: any, request: Parameters<typeof fetchFreezes>[0]): Promise<boolean> {
    return confirmFreezes(vm, await fetchFreezes(request));
}

/**
 * Kaydedilecek islerin freeze'e denk gelip gelmedigini sunucuya sorar. Ekran kaydedecegi tarihi
 * (StartDate) ve sureyi (Duration, dakika) oldugu gibi gonderir: tarih, kayittaki gibi sunucuda
 * cozulur, ekranin bicimini burada yeniden yorumlamak gerekmez.
 */
export async function confirmFreezeConflicts(vm: any, windows: CFMDTOFreezeWindow[]): Promise<boolean> {
    const valid = windows.filter(window => window.StartDate && window.Duration && window.Duration > 0);
    if (valid.length === 0) {
        return true;
    }
    return confirmFreezesFrom(vm, vm.$globalApiClient.freeze.freezeGetConflicts(valid));
}
